import { useMemo } from 'react'
import { createFileRoute, redirect, Link } from '@tanstack/react-router'
import { useQuery } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { ArrowLeft, ArrowUpRight, FileText } from 'lucide-react'

import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { requireOrganizationFeature } from '@/lib/route-preloading'
import { isPermissionDeniedError } from '@/lib/error-reporting'
import { useInvoice, useInvoiceLitesByIds } from '@/stores/invoices.store'
import { useAttachments } from '@/stores/attachments.store'
import { useContact } from '@/stores/contacts.store'
import { getJob } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { GetJobRequestSchema } from '@/gen/cortado/v1/jobs_pb'
import { OrganizationFeatureEnum, ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { Button } from '@/components/ui/button'
import { SkeletonBar } from '@/components/shared/skeleton-bar'
import { Empty, EmptyDescription, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { AccessDeniedState } from '@/components/shared/access-denied-state'
import { ActivityTabs } from '@/components/shared/activity-tabs'
import { InvoiceStatusBadge } from '@/components/shared/enum-badge'
import { LinkedRecordCard } from '@/components/shared/financial-document'
import { InvoiceWorkspace } from '@/components/invoices/invoice-workspace'
import { JobRail } from '@/components/pipeline/job-rail'
import { JobContactCard } from '@/components/pipeline/job-contact-card'
import { JobInvoiceSwitcher } from '@/components/pipeline/job-invoice-switcher'
import { jobOwnsInvoice } from '@/components/pipeline/job-detail-view'
import { jobChildBreadcrumbs, jobLabel } from '@/components/pipeline/pipeline-breadcrumbs'

const NO_IDS: string[] = []

// `$id_` opts out of nesting under the job page route: this is its own page,
// not an outlet inside the job detail.
export const Route = createFileRoute('/_app/_home/pipeline/$id_/invoices/$invoiceId')({
  // Without the Pipeline, the invoice still has its standalone page.
  beforeLoad: ({ context, params }) =>
    requireOrganizationFeature(context, OrganizationFeatureEnum.ORGANIZATION_FEATURE_JOBS, () =>
      redirect({ to: '/invoices/$id', params: { id: params.invoiceId }, replace: true }),
    ),
  component: PipelineInvoiceRouteFromParams,
})

function PipelineInvoiceRouteFromParams() {
  const { id, invoiceId } = Route.useParams()
  return <PipelineInvoicePage jobId={id} invoiceId={invoiceId} />
}

function NotFound({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex flex-1 flex-col rounded-lg border border-dashed py-6 md:py-0">
        <Empty>
          <EmptyMedia variant="icon">
            <FileText className="h-6 w-6" />
          </EmptyMedia>
          <EmptyTitle>{title}</EmptyTitle>
          <EmptyDescription>{description}</EmptyDescription>
        </Empty>
      </div>
    </div>
  )
}

/**
 * One invoice of a pipeline job: line items, payment schedules, payments and
 * the invoice's files, conversations and portal, with the job's contact, tasks, notes
 * and feed alongside.
 * Jobs with more than one invoice get a tab row to move between them.
 */
export function PipelineInvoicePage({ jobId, invoiceId }: { jobId: string; invoiceId: string }) {
  const jobRequest = useMemo(() => create(GetJobRequestSchema, { jobId }), [jobId])
  const jobQuery = useQuery(getJob, jobRequest, { enabled: Boolean(jobId) })
  const job = jobQuery.data?.job
  const { invoice, isLoading: isInvoiceLoading, isError: isInvoiceError, error: invoiceError, refetch } = useInvoice(invoiceId)
  const { invoices } = useInvoiceLitesByIds(job?.invoiceIds ?? NO_IDS)
  const activityParent = { parentType: ParentTypeEnum.PARENT_TYPE_INVOICE, parentId: invoiceId }
  const { attachments } = useAttachments(activityParent)
  // The invoice's contact is who Compose on its Conversations tab writes to.
  const { contact } = useContact(invoice?.contactId)
  const breadcrumbs = useMemo(
    () => (job && invoice ? jobChildBreadcrumbs(job, `Invoice #${invoice.invoiceNumber}`) : undefined),
    [job, invoice],
  )
  useBreadcrumbFromRoute(breadcrumbs)

  if (jobQuery.isLoading || isInvoiceLoading) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <div className="space-y-2">
          <SkeletonBar className="h-4 w-48" />
          <SkeletonBar className="h-8 w-64" />
        </div>
        <div className="grid gap-4 xl:grid-cols-[1fr_360px]">
          <SkeletonBar className="h-96 w-full rounded-xl" />
          <SkeletonBar className="h-64 w-full rounded-xl" />
        </div>
      </div>
    )
  }

  if (jobQuery.isError || !job) {
    return <NotFound title="Job not found" description="We couldn't load this job. It may have been removed." />
  }
  // A 403 is a role denial, not a missing invoice.
  if (isPermissionDeniedError(invoiceError)) {
    return (
      <div className="flex h-full flex-col p-4">
        <AccessDeniedState variant="page" noun="invoices" />
      </div>
    )
  }
  // An invoice id from another job (a stale or hand-edited URL) is not this job's.
  if (isInvoiceError || !invoice || !jobOwnsInvoice(job, invoice)) {
    return <NotFound title="Invoice not found" description="This invoice doesn't exist or belongs to a different job." />
  }

  const sourceQuoteId = invoice.quoteId && job.quoteIds.includes(invoice.quoteId) ? invoice.quoteId : undefined
  const recipientName = [job.contactFirstName, job.contactLastName].filter(Boolean).join(' ')

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-4 pb-8">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Invoice #{invoice.invoiceNumber}</h1>
          <InvoiceStatusBadge status={invoice.status} />
        </div>
        {invoice.title && <div className="mt-1.5 text-xs/normal text-muted-foreground">{invoice.title}</div>}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs/normal text-muted-foreground">
          <Link to="/pipeline/$id" params={{ id: job.id }} className="inline-flex items-center gap-1 hover:text-foreground">
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to {jobLabel(job)}
          </Link>
          <Link to="/invoices/$id" params={{ id: invoice.id }} className="inline-flex items-center gap-1 hover:text-foreground">
            Full invoice page
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      <JobInvoiceSwitcher jobId={job.id} invoices={invoices} currentInvoiceId={invoice.id} />

      <InvoiceWorkspace
        invoice={invoice}
        recipientName={recipientName}
        refetch={refetch}
        main={
          // Notes and tasks live in the rail's job cards, which include the invoice's own.
          <ActivityTabs
            title="Invoice activity"
            description="Files, conversations and portal"
            organizationId={invoice.organizationId}
            parentType={ParentTypeEnum.PARENT_TYPE_INVOICE}
            parentId={invoice.id}
            hideNotesAndTasks
            attachments={attachments}
            customerEmail={contact?.email}
            emailContactId={invoice.contactId}
          />
        }
        aside={
          <>
            <JobContactCard job={job} />
            <LinkedRecordCard
              title="Linked records"
              rows={[
                {
                  label: 'Created from quote',
                  value: sourceQuoteId ? 'Source quote' : 'Standalone invoice',
                  action: sourceQuoteId ? (
                    <Button variant="ghost" size="icon" asChild>
                      <Link
                        to="/pipeline/$id/quotes/$quoteId"
                        params={{ id: job.id, quoteId: sourceQuoteId }}
                        aria-label="Open source quote"
                      >
                        <ArrowUpRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  ) : undefined,
                },
              ]}
            />
            <JobRail job={job} stage="invoice" />
          </>
        }
      />
    </div>
  )
}
